<script setup lang="ts">
import type { VoixMedia, VoixMediaImage, VoixMediaVideo } from '@voix/types'
import type { InjectionKey, PropType, Ref } from 'vue'

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import SpinnerLoader from '@voix/components/chrome/Loader.vue'

import { mediaSelected } from '@voix/composables/queries/useQueryMedia'
import { inView } from 'motion'
import { computed, inject, onMounted, ref } from 'vue'
import ImageTags from './../Tags.vue'

const props = defineProps({
  media: {
    type: Object as PropType<VoixMediaImage | VoixMediaVideo | VoixMedia>,
    required: true,
  },
  selectedFileId: {
    type: String,
    default: null,
  },
  viewMode: {
    type: String as PropType<'grid' | 'list'>,
    required: true,
  },
})

const emit = defineEmits(['select', 'deselect'])

const provider: any = inject('provider')
const mediaBrowseMode: boolean | undefined = inject('mediaBrowseMode', false)

const breakpointNames = {
  'default': 'Smallest',
  'sm': 'Mobile',
  'md': 'Mobile/Tablet',
  'lg': 'Tablet/Desktop',
  'xl': 'Large Desktop',
  '2xl': 'XL Desktop',
}

const previewLoaded = ref(false)
const shouldLoadPreview = ref(false)
const mediaLoaded = ref(false)
const mediaSelectLoading = ref(false)

const fieldConfiguration: any = inject('fieldConfiguration', null)
const selectMedia: any = inject('selectMedia')

const isSelected = computed(() => {
  return props.selectedFileId === props.media.id
})

function requestSelect() {
  if (!isSelected.value)
    emit('select', props.media.id)
}

function requestDeselect() {
  emit('deselect')
}

async function onMediaSelect(breakpointName: string) {
  if (!provider?.value)
    return

  mediaSelectLoading.value = true

  const { data }: { data: any } = await mediaSelected({
    id: props.media.id,
    client: provider.value,
    url: props.media.url,
  })

  const mediaData = Object.assign({ final_url: data.value.url }, props.media)
  selectMedia(breakpointName, mediaData)
}

function downloadMedia() {
  // Get the filename from the URL
  const filename = props.media.url.split('/').pop()

  if (filename) {
    fetch(props.media.url)
      .then(response => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        // link.target = '_blank'

        link.setAttribute('download', props.media.name || props.media.id || filename)

        // Append the link to the document and trigger a click
        document.body.appendChild(link)
        link.click()

        // Remove the link after triggering the download
        document.body.removeChild(link)
      })
      .catch(() => {
        if (window) {
          window.open(props.media.url, '_blank')
        }
      })
  }
}

const mainElement = ref(null)
const showTimer: Ref<any> = ref(null)

onMounted(() => {
  if (!mainElement.value)
    return

  inView(mainElement.value, (info) => {
    if (info.isIntersecting) {
      showTimer.value = setTimeout(() => {
        shouldLoadPreview.value = true
      }, 1000)
    }

    return () => {
      if (showTimer.value)
        clearTimeout(showTimer.value)
    }
  })
})
</script>

<template>
  <div ref="mainElement" class="relative">
    <div>
      <div
        class="w-full px-3 py-3 rounded-lg text-sm text-gray-500 hover:bg-gray-100 cursor-pointer"
        :class="{
          'grid grid-cols-12 gap-8': viewMode === 'list',
        }"
      >
        <!-- Thumbnail Preview -->
        <div
          class="relative overflow-hidden rounded flex-none bg-gray-300"
          :class="{
            'w-full aspect-video': viewMode === 'grid',
            'w-16 h-16 col-span-2': viewMode === 'list',
          }"

          @click="requestSelect"
        >
          <div
            v-if="!previewLoaded"
            class="pointer-events-none absolute inset-0 flex flex-col space-y-1 justify-center items-center opacity-75"
          >
            <SpinnerLoader class="bg-white" />
          </div>

          <template v-if="media.type === 'image'">
            <!-- Preview Thumb -->
            <NuxtImg
              v-if="shouldLoadPreview"
              :src="props.media.thumb"
              :alt="props.media.id"
              class="w-full h-full transition duration-1000"
              :class="{
                'object-cover': !isSelected,
                'object-contain': isSelected,
                'opacity-0': !previewLoaded,
                'opacity-100': previewLoaded,
              }"
              loading="lazy"
              width="182"
              height="128"
              fit="cover"
              quality="40"
              @load="previewLoaded = true"
            />
          </template>
          <template v-if="media.type === 'video'">
            <img
              :src="props.media.thumb"
              :alt="props.media.id"
              class="w-full h-full object-cover transition duration-1000"
              :class="{
                'opacity-0': !previewLoaded,
                'opacity-100': previewLoaded,
              }"
              @load="previewLoaded = true"
            >
          </template>
        </div>
        <div v-if="viewMode === 'list'" class="pl-4 col-span-10">
          <div class="text-base font-medium voix-admin-text-dark">
            {{ props.media.name }}
          </div>
          <div class="mt-1 grid grid-cols-1 xl:grid-cols-3 uppercase text-xs gap-1">
            <span><span class="text-gray-400">Type: </span>{{ props.media.mime_type }}</span>
            <span><span class="text-gray-400">Width: </span>{{ props.media.width }}px</span>
            <span><span class="text-gray-400">Height: </span>{{ props.media.height }}px</span>
          </div>
        </div>

        <!-- Full Preview and Selection -->
        <div v-if="isSelected" class="fixed inset-0 z-10 overflow-hidden p-12">
          <div class="absolute inset-0 z-0 bg-black/70" />

          <div

            class="relative z-10 w-full h-full overflow-hidden rounded flex-none bg-gray-800 p-4 pb-24"
          >
            <button
              v-if="isSelected"
              class="absolute z-30 top-6 right-6 text-white p-1 bg-white/10 rounded transition duration-200 hover:bg-white/30"
              @click="requestDeselect"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <template v-if="media.type === 'image'">
              <div
                v-if="!mediaLoaded || mediaSelectLoading"
                class="pointer-events-none absolute inset-0 flex justify-center items-center flex-col"
                :class="{
                  'opacity-75': !mediaLoaded,
                  'opacity-100': mediaSelectLoading,
                }"
              >
                <SpinnerLoader class="bg-white" />
                <div class="mt-2 text-center font-medium text-white text-xs">
                  Loading...
                </div>
              </div>
              <!-- Full Preview -->
              <NuxtImg
                v-if="isSelected"
                :src="isSelected ? props.media.url : props.media.thumb"
                :alt="props.media.id"
                class="w-full h-full transition duration-1000"
                :class="{
                  'object-cover': !isSelected,
                  'object-contain': isSelected,
                  'opacity-0': !mediaLoaded,
                  'opacity-15': mediaSelectLoading,
                  'opacity-100': mediaLoaded,
                }"
                loading="lazy"
                @load="mediaLoaded = true"
              />
            </template>
            <template v-if="media.type === 'video'">
              <div
                v-if="mediaSelectLoading"
                class="pointer-events-none absolute inset-0 flex justify-center items-center flex-col"
                :class="{
                  'opacity-75': !mediaLoaded,
                  'opacity-100': mediaSelectLoading,
                }"
              >
                <SpinnerLoader class="bg-white" />
                <div class="mt-2 text-center font-medium text-white text-xs">
                  Loading...
                </div>
              </div>

              <video
                v-if="isSelected"
                :src="props.media.url"
                :alt="props.media.id"
                class="w-full h-full object-contain transition duration-1000"
                :class="{
                  'opacity-15': mediaSelectLoading,
                }"
                controls
                autoplay
                muted
              />
            </template>
            <div
              class="absolute z-10 bottom-0 left-0 right-0 voix-admin-bg-lightest voix-admin-text-dark p-4 flex items-center justify-between "
            >
              <div
                class="voix-admin-text font-medium break-all overflow-hidden text-lg "
              >
                <div>{{ props.media?.name ? props.media.name : props.media.id }}</div>
                <div v-if="props.media?.mime_type" class=" voix-admin-text text-xs uppercase">
                  TYPE: {{ props.media?.mime_type ? props.media.mime_type : 'Unknown' }}
                </div>
              </div>
              <div
                class="text-center text-base"
              >
                <div class="font-bold uppercase text-xs">
                  Source Size
                </div>
                <div>{{ props.media.width }}px x {{ props.media.height }}px</div>
              </div>
              <div class="relative flex items-center space-x-2">
                <button

                  class="relative rounded-full flex justify-between voix-admin-bg text-white p-2 text-xs font-bold"
                  @click="downloadMedia"
                >
                  <Icon name="heroicons:arrow-down-20-solid" class="w-6 h-6" />
                </button>
                <template v-if="!mediaBrowseMode">
                  <button
                    v-if="!fieldConfiguration"
                    class="relative rounded-full flex justify-between space-x-3 voix-admin-bg text-white px-5 py-3 text-xs font-bold"
                    @click="onMediaSelect('default')"
                  >
                    <span v-if="props.media.type === 'image'">Use Image </span>
                    <span v-if="props.media.type === 'video'">Use Video </span>
                  </button>
                  <template v-if="fieldConfiguration">
                    <Menu v-if="fieldConfiguration.breakpoints && Object.keys(fieldConfiguration.breakpoints).length > 1">
                      <MenuButton
                        class="relative rounded flex justify-between space-x-3 voix-admin-bg text-white px-5 py-3 text-xs font-bold"
                        :disabled="mediaSelectLoading"
                      >
                        <span v-if="props.media.type === 'image'">Use Image </span>
                        <span v-if="props.media.type === 'video'">Use Video </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke-width="4"
                          stroke="currentColor"
                          class="w-4 h-4"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                          /></svg>
                      </MenuButton>
                      <Transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-out"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                      >
                        <MenuItems
                          class="absolute z-30 bottom-0 right-0 transform -translate-y-14 rounded-lg bg-white flex flex-col shadow-lg w-56"
                        >
                          <MenuItem
                            v-for="(
                              breakpoint, breakpointName
                            ) in fieldConfiguration.breakpoints"
                            :key="breakpointName"
                            v-slot="{ active }"
                            class="px-4 py-2"
                          >
                            <button
                              class="whitespace-nowrap"
                              :class="{ 'voix-admin-bg-lightest rounded-md': active }"
                              @click="onMediaSelect(breakpointName)"
                            >
                              For
                              <span class="font-bold">{{ breakpointNames[breakpointName] }}</span>
                              and Up
                            </button>
                          </MenuItem>
                        </MenuItems>
                      </Transition>
                    </Menu>
                    <button
                      v-else
                      class="relative rounded flex justify-between space-x-3 voix-admin-bg text-white text-xs font-bold"
                    >
                      <template v-if="fieldConfiguration.breakpoints">
                        <span v-if="props.media.type === 'image'" class="px-5 py-3 " @click="onMediaSelect(Object.keys(fieldConfiguration.breakpoints)[0])">Use Image </span>
                        <span v-if="props.media.type === 'video'" class="px-5 py-3 " @click="onMediaSelect(Object.keys(fieldConfiguration.breakpoints)[0])">Use Video </span>
                      </template>
                      <template v-else>
                        <span v-if="props.media.type === 'image'" class="px-5 py-3 " @click="onMediaSelect('default')">Use Image </span>
                        <span v-if="props.media.type === 'video'" class="px-5 py-3 " @click="onMediaSelect('default')">Use Video </span>
                      </template>
                    </button>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isSelected && props.media.tags"
        class="mt-4 w-full text-left"
      >
        <ImageTags class="col-span-4" :tags="props.media.tags" />
      </div>
    </div>
  </div>
</template>
